import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import { tx } from '../../lib/adminI18n';

// 設定 → 広告クーポン タブ   設計: .plan/repeater-coupon-impl-v1.md §17
//   API: GET/POST /api/admin/campaign-coupons, PUT /:code, PATCH /:code/active
//   ・複数同時に有効。割引率のみ。先着の上限・1人あたりの回数・最低泊数・最低金額・対象施設を設定できる。
//   ・利用数は予約から数える（期限切れ・キャンセル・返金は数えない）。売上・割引総額は確定済み予約のみ。

type Status = 'ACTIVE' | 'PAUSED' | 'NOT_STARTED' | 'EXPIRED' | 'SOLD_OUT';
interface Row {
  code: string; name: string | null; channel: string | null; discount_value: number;
  valid_from: string | null; valid_until: string | null; usage_limit: number | null; per_guest_limit: number | null;
  min_nights: number | null; min_amount: number | null; target_property_id: string | null; note: string | null;
  is_active: number; updated_at: string | null; updated_by: string | null;
  used: number; confirmed_count: number; revenue: number; discount_total: number; status: Status;
}
interface Prop { id: string; name: string; }
type Form = Record<'code' | 'name' | 'channel' | 'discount_rate' | 'valid_from' | 'valid_until' | 'usage_limit' |
  'per_guest_limit' | 'min_nights' | 'min_amount' | 'target_property_id' | 'note', string>;

const EMPTY: Form = { code: '', name: '', channel: '', discount_rate: '10', valid_from: '', valid_until: '',
  usage_limit: '', per_guest_limit: '1', min_nights: '', min_amount: '', target_property_id: '', note: '' };
const STATUS_COLOR: Record<Status, string> = {
  ACTIVE: '#059669', PAUSED: '#d97706', NOT_STARTED: '#2563eb', EXPIRED: '#6b7280', SOLD_OUT: '#dc2626',
};
const SITE = 'https://booking.staydreamhotel.com';
const d10 = (v: string | null) => (v ? String(v).slice(0, 10) : '');
const yen = (n: number) => `¥${Number(n || 0).toLocaleString()}`;
const s = (v: number | null) => (v == null ? '' : String(v));

export default function CampaignCoupons() {
  const [rows, setRows] = useState<Row[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [maxRate, setMaxRate] = useState(30);
  const [props, setProps] = useState<Prop[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null); // null=閉じる / '' = 新規 / code = 編集
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch('/api/admin/campaign-coupons', { headers: adminHeaders(), credentials: 'include' }),
        fetch('/api/properties'),
      ]);
      const d = await r1.json().catch(() => ({}));
      if (!r1.ok) { setMsg({ ok: false, text: tx('cp.loadErr') }); return; }
      setRows(d.coupons || []); setChannels(d.channels || []); setMaxRate(d.max_rate || 30);
      if (r2.ok) setProps(((await r2.json()) || []).map((p: any) => ({ id: p.id, name: p.name })));
    } catch { setMsg({ ok: false, text: tx('cp.loadErr') }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    n: a.n + Number(r.confirmed_count || 0), rev: a.rev + Number(r.revenue || 0), dis: a.dis + Number(r.discount_total || 0),
  }), { n: 0, rev: 0, dis: 0 }), [rows]);

  const openNew = () => { setForm(EMPTY); setEditing(''); setMsg(null); };
  const openEdit = (r: Row) => {
    setForm({ code: r.code, name: r.name || '', channel: r.channel || '', discount_rate: String(r.discount_value),
      valid_from: d10(r.valid_from), valid_until: d10(r.valid_until), usage_limit: s(r.usage_limit),
      per_guest_limit: s(r.per_guest_limit), min_nights: s(r.min_nights), min_amount: s(r.min_amount),
      target_property_id: r.target_property_id || '', note: r.note || '' });
    setEditing(r.code); setMsg(null);
  };
  const set = (k: keyof Form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    const isNew = editing === '';
    const cur = rows.find((r) => r.code === editing);
    try {
      const res = await fetch(isNew ? '/api/admin/campaign-coupons' : `/api/admin/campaign-coupons/${encodeURIComponent(editing!)}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ ...form, code: form.code.trim().toUpperCase(),
          discount_rate: Number(form.discount_rate), is_active: isNew ? true : Number(cur?.is_active) === 1 }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: d.error || tx('cp.saveErr') }); return; }
      setEditing(null); setMsg({ ok: true, text: tx('cp.saved') });
      await load();
    } catch (e: any) { setMsg({ ok: false, text: e?.message || tx('cp.saveErr') }); }
    finally { setBusy(false); }
  };

  const toggle = async (r: Row) => {
    const next = Number(r.is_active) !== 1;
    if (!next && !window.confirm(tx('cp.confirmPause', { s: r.code }))) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/campaign-coupons/${encodeURIComponent(r.code)}/active`, {
        method: 'PATCH', headers: adminHeaders({ 'Content-Type': 'application/json' }), credentials: 'include',
        body: JSON.stringify({ is_active: next }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg({ ok: false, text: d.error || tx('cp.saveErr') }); }
      await load();
    } finally { setBusy(false); }
  };

  const linkOf = (code: string) => `${SITE}/booking?coupon=${encodeURIComponent(code)}`;
  const copyLink = async (code: string) => {
    try { await navigator.clipboard.writeText(linkOf(code)); setCopied(code); setTimeout(() => setCopied(null), 1500); } catch { /* noop */ }
  };
  const propName = (id: string | null) => (id ? (props.find((p) => p.id === id)?.name || id) : tx('cc.allProps'));

  if (loading) return <p className="set-loading">{tx('set.31')}</p>;

  const field = (k: keyof Form, label: string, extra?: { type?: string; hint?: string; ph?: string; disabled?: boolean; min?: number; max?: number }) => (
    <div className="set-group">
      <label className="set-label" htmlFor={`cc-${k}`}>{label}</label>
      <input id={`cc-${k}`} className="set-input" type={extra?.type || 'text'} value={form[k]} placeholder={extra?.ph}
        disabled={extra?.disabled} min={extra?.min} max={extra?.max}
        onChange={(e) => set(k, k === 'code' ? e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') : e.target.value)} />
      {extra?.hint && <small className="set-hint">{extra.hint}</small>}
    </div>
  );

  return (
    <>
      <section className="set-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 className="set-section-title" style={{ margin: 0 }}>{tx('cc.title')}</h2>
          {editing === null && <button className="set-btn set-btn-primary" onClick={openNew}>＋ {tx('cc.new')}</button>}
        </div>
        <p className="set-hint" style={{ display: 'block', margin: '8px 0 12px' }}>{tx('cc.desc')}</p>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14, marginBottom: 8 }}>
          <span>{tx('cc.totalBookings')}: <b>{totals.n}</b></span>
          <span>{tx('cc.totalRevenue')}: <b>{yen(totals.rev)}</b></span>
          <span>{tx('cc.totalDiscount')}: <b>{yen(totals.dis)}</b></span>
        </div>
        {msg && <div className={msg.ok ? 'set-msg set-msg-ok' : 'set-msg set-msg-err'}>{msg.text}</div>}
      </section>

      {editing !== null && (
        <section className="set-section">
          <h2 className="set-section-title">{editing === '' ? tx('cc.new') : `${tx('cc.edit')}: ${editing}`}</h2>
          <div className="set-grid">
            {field('code', tx('cp.code'), { ph: 'SPRING-IG10', disabled: editing !== '', hint: editing === '' ? tx('cc.codeHint') : tx('cc.codeFixed') })}
            {field('name', tx('cc.name'), { ph: tx('cc.namePh') })}
            <div className="set-group">
              <label className="set-label" htmlFor="cc-channel">{tx('cc.channel')}</label>
              <select id="cc-channel" className="set-input" value={form.channel} onChange={(e) => set('channel', e.target.value)}>
                <option value="">-</option>
                {channels.map((c) => <option key={c} value={c}>{tx(`cc.ch.${c}` as any)}</option>)}
              </select>
            </div>
            {field('discount_rate', tx('cp.rate'), { type: 'number', min: 1, max: maxRate, hint: tx('cp.rateHint', { n: maxRate }) })}
            {field('valid_from', tx('cp.from'), { type: 'date' })}
            {field('valid_until', tx('cp.until'), { type: 'date', hint: tx('cp.periodHint') })}
            {field('usage_limit', tx('cc.usageLimit'), { type: 'number', min: 1, hint: tx('cc.usageLimitHint') })}
            {field('per_guest_limit', tx('cc.perGuest'), { type: 'number', min: 1, hint: tx('cc.perGuestHint') })}
            {field('min_nights', tx('cc.minNights'), { type: 'number', min: 1, hint: tx('cc.emptyNoLimit') })}
            {field('min_amount', tx('cc.minAmount'), { type: 'number', min: 1, hint: tx('cc.minAmountHint') })}
            <div className="set-group">
              <label className="set-label" htmlFor="cc-prop">{tx('cc.target')}</label>
              <select id="cc-prop" className="set-input" value={form.target_property_id} onChange={(e) => set('target_property_id', e.target.value)}>
                <option value="">{tx('cc.allProps')}</option>
                {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {field('note', tx('cp.note'))}
          </div>
          <div className="set-actions">
            <button className="set-btn set-btn-ghost" disabled={busy} onClick={() => setEditing(null)}>{tx('cc.cancel')}</button>
            <button className="set-btn set-btn-primary"
              disabled={busy || !form.code || !form.name || !form.valid_from || !form.valid_until} onClick={save}>
              {busy ? tx('cp.saving') : tx('cp.save')}
            </button>
          </div>
        </section>
      )}

      <section className="set-section">
        {rows.length === 0 ? <p className="set-hint">{tx('cc.empty')}</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 820 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb', color: '#374151' }}>
                  <th style={{ padding: 8 }}>{tx('cp.status')}</th>
                  <th style={{ padding: 8 }}>{tx('cp.code')} / {tx('cc.name')}</th>
                  <th style={{ padding: 8 }}>{tx('cp.rate')}</th>
                  <th style={{ padding: 8 }}>{tx('cc.period')}</th>
                  <th style={{ padding: 8 }}>{tx('cc.used')}</th>
                  <th style={{ padding: 8 }}>{tx('cc.revenue')}</th>
                  <th style={{ padding: 8 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.code} style={{ borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                    <td style={{ padding: 8 }}>
                      <span style={{ color: '#fff', background: STATUS_COLOR[r.status], borderRadius: 12, padding: '2px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {tx(`cc.st.${r.status}` as any)}
                      </span>
                    </td>
                    <td style={{ padding: 8 }}>
                      <code style={{ fontWeight: 700 }}>{r.code}</code>
                      <div>{r.name}{r.channel ? <span style={{ color: '#6b7280' }}> · {tx(`cc.ch.${r.channel}` as any)}</span> : null}</div>
                      <small style={{ color: '#6b7280' }}>
                        {propName(r.target_property_id)}
                        {r.min_nights ? ` · ${tx('cc.minNightsShort', { n: r.min_nights })}` : ''}
                        {r.min_amount ? ` · ${tx('cc.minAmountShort', { s: yen(r.min_amount) })}` : ''}
                        {r.per_guest_limit ? ` · ${tx('cc.perGuestShort', { n: r.per_guest_limit })}` : ''}
                      </small>
                    </td>
                    <td style={{ padding: 8 }}>{r.discount_value}%</td>
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{d10(r.valid_from)}<br />〜 {d10(r.valid_until)}</td>
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{r.used}{r.usage_limit ? ` / ${r.usage_limit}` : ''}</td>
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                      {tx('cc.bookingsN', { n: r.confirmed_count })}<br />{yen(r.revenue)}
                      <br /><small style={{ color: '#6b7280' }}>-{yen(r.discount_total)}</small>
                    </td>
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button className="set-btn set-btn-ghost" disabled={busy} onClick={() => copyLink(r.code)}>
                          {copied === r.code ? tx('cp.copied') : tx('cc.copyLink')}
                        </button>
                        <button className="set-btn set-btn-ghost" disabled={busy} onClick={() => openEdit(r)}>{tx('cc.edit')}</button>
                        <button className="set-btn set-btn-ghost" disabled={busy} onClick={() => toggle(r)}>
                          {Number(r.is_active) === 1 ? tx('cp.pause') : tx('cp.resume')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
